const KEY = "zephr-modal";

const INTENT_MAX_AGE_MS = 15 * 60 * 1000;
const AVAILABILITY_TIMEOUT_MS = 10_000;
const AVAILABILITY_POLL_MS = 100;

type WhenAvailableOptions = {
  timeoutMs?: number;
  signal?: AbortSignal;
};

const getStorage = (): Storage | null => {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const openLater = () => {
  getStorage()?.setItem(KEY, String(Date.now()));
};

const cancelOpenLater = () => {
  getStorage()?.removeItem(KEY);
};

const isPending = () => {
  const storedAt = getStorage()?.getItem(KEY);

  if (!storedAt) {
    return false;
  }

  const requestedAt = Number(storedAt);

  if (
    !Number.isFinite(requestedAt) ||
    Date.now() - requestedAt > INTENT_MAX_AGE_MS
  ) {
    cancelOpenLater();
    return false;
  }

  return true;
};

const isAvailable = () =>
  typeof window !== "undefined" &&
  typeof window.Zephr?.openModal === "function";

const whenAvailable = ({
  timeoutMs = AVAILABILITY_TIMEOUT_MS,
  signal,
}: WhenAvailableOptions = {}): Promise<boolean> =>
  new Promise((resolve) => {
    if (isAvailable()) {
      resolve(true);
      return;
    }

    if (signal?.aborted || typeof window === "undefined") {
      resolve(false);
      return;
    }

    const startedAt = Date.now();

    const stop = (available: boolean) => {
      window.clearInterval(intervalId);
      signal?.removeEventListener("abort", onAbort);
      resolve(available);
    };

    const onAbort = () => stop(false);

    const intervalId = window.setInterval(() => {
      if (isAvailable()) {
        stop(true);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        stop(false);
      }
    }, AVAILABILITY_POLL_MS);

    signal?.addEventListener("abort", onAbort);
  });

// Zephr CDN's close-on-backdrop-click handler sets `display:none` on the dialog
// instead of calling `dialog.close()`. A native <dialog> opened via showModal()
// stays in the browser top layer when only hidden via CSS, which makes it
// intercept all pointer events and block the entire page. This observer detects
// that state and calls dialog.close() to properly remove it from the top layer.
const releaseScrollLock = () => {
  const { body } = document;

  if (body.style.position !== "fixed") {
    return;
  }

  const scrollY = -parseInt(body.style.top || "0", 10) || 0;

  for (const property of [
    "overflow",
    "position",
    "top",
    "width",
    "padding-right",
  ]) {
    body.style.removeProperty(property);
  }

  window.scrollTo(0, scrollY);
};

const watchForImproperDialogClose = () => {
  const attach = (dialog: HTMLDialogElement) => {
    const isVisible = () =>
      dialog.open && getComputedStyle(dialog).display !== "none";

    let wasVisible = isVisible();

    const observer = new MutationObserver(() => {
      if (isVisible()) {
        wasVisible = true;
        return;
      }

      if (!wasVisible) {
        return;
      }

      if (dialog.open) {
        dialog.close();
      }

      releaseScrollLock();
      observer.disconnect();
    });
    observer.observe(dialog, {
      attributes: true,
      attributeFilter: ["style", "class", "open"],
    });
  };

  const existing = document.querySelector<HTMLDialogElement>(
    "dialog.zephr-zone-popup"
  );
  if (existing) {
    attach(existing);
    return;
  }

  // Dialog may be injected asynchronously by Zephr CDN
  const domObserver = new MutationObserver(() => {
    const dialog = document.querySelector<HTMLDialogElement>(
      "dialog.zephr-zone-popup"
    );
    if (dialog) {
      domObserver.disconnect();
      attach(dialog);
    }
  });
  domObserver.observe(document.body, { childList: true, subtree: true });
  // Safety: stop watching after 10 s if dialog never appears
  setTimeout(() => domObserver.disconnect(), 10_000);
};

const open = (onOpenCallback?: () => void) => {
  cancelOpenLater();
  window.Zephr.openModal();
  onOpenCallback?.();
  watchForImproperDialogClose();
};

const openWhenAvailable = async (
  onOpenCallback?: () => void,
  options?: WhenAvailableOptions
) => {
  const available = await whenAvailable(options);

  if (!available) {
    return false;
  }

  open(onOpenCallback);
  return true;
};

const close = () => {
  cancelOpenLater();

  if (typeof window.Zephr?.closeModal === "function") {
    window.Zephr.closeModal();
  }

  releaseScrollLock();
};

export const zephrModal = {
  isAvailable,
  isPending,
  open,
  openWhenAvailable,
  whenAvailable,
  close,
  openLater,
  cancelOpenLater,
};
